import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import CTABand from "@/components/recruit/CTABand";

const JOB_INFO = [
  { label: "職種", value: "物件開拓営業（Franchise Development Sales）" },
  { label: "雇用形態", value: "正社員" },
  { label: "給与", value: "500～800万円 + 四半期インセンティブ（上限なし）" },
  { label: "勤務地", value: "東京都千代田区富士見2-10-2 飯田橋グラン・ブルーム9F（リモートワーク中心・出社週２回）" },
  { label: "勤務時間", value: "10:00～19:00（休憩1時間）※ライフスタイルに合わせて調整可能" },
  { label: "休日", value: "完全週休２日制 / 年間休日120日 / 祈日休み" },
  { label: "残業", value: "月平剠20時間以内" },
  { label: "試用期間", value: "4ヶ月" },
];

const REQUIRED_SKILLS = [
  "法人営業またはB2B営業経験（3年以上目安）",
  "関係構築型の営業スタイルでの実績",
  "日本語ネイティブレベル",
  "基本的なPCスキル（メール、スプレッドシート等）",
];

const PREFERRED_SKILLS = [
  "不動産業界での経験",
  "ホスピタリティ業界での経験",
  "フランチャイズ営業または代理店営業の経験",
  "新規開拓営業の経験",
  "英語力（社内コミュニケーションで使用する場合あり）",
];

const FAQ_ITEMS = [
  { q: "業界未経験でも応募できますか？", a: "はい。不動産やホスピタリティの経験は歓迎しますが、必須ではありません。B2B営業や関係構築型の営業経験があれば、業界知識は入社後にキャッチアップできます。" },
  { q: "ノルマはありますか？", a: "厳密なノルマではなく、目標ベースで進めます。代表と相談しながら現実的な目標を設定し、達成に向けて一緒に取り組みます。" },
  { q: "リモートワークの割合はどのくらいですか？", a: "基本的にリモートワーク中心で、出社は週２回程度を想定しています。物件視察やオーナーとの面談は外出ベースになります。" },
  { q: "インセンティブの詳細を教えてください。", a: "四半期ごとに支給。成約した物件の規模（ADR × 室数）に基づいて算出されます。上限はありません。詳しくはカジュアル面談でお話しします。" },
  { q: "どのような選考プロセスですか？", a: "カジュアル面談 → 適性検査 → 面接（1-2回）→ 内定 → 入社。" },
  { q: "入社時期は相談できますか？", a: "はい、柔軟に対応します。現職の退職時期に合わせた入社日の調整が可能です。" },
  { q: "将来的にマネジメントポジションはありますか？", a: "はい。マネジメントトラックとプロフェッショナルトラックの２つのキャリアパスがあり、どちらも事業責任者・役員への道が開かれています。" },
  { q: "外国籍でも応募できますか？", a: "はい。日本語でのビジネスコミュニケーションが可能であれば、国籍は問いません。" },
];

function FAQItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-acoe-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
        data-testid={`faq-toggle`}
      >
        <span className="font-heading font-semibold text-acoe-charcoal pr-4 group-hover:text-acoe-gold transition-colors">{item.q}</span>
        <ChevronDown size={20} className={`text-acoe-muted flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      {open && (
        <div className="pb-5">
          <p className="text-sm text-acoe-text leading-relaxed">{item.a}</p>
        </div>
      )}
    </div>
  );
}

export default function CareersPage() {
  useEffect(() => { document.title = "募集要項 | ACOE 採用サイト"; }, []);

  return (
    <div data-testid="careers-page">
      {/* ─── HERO ─── */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-acoe-charcoal" data-testid="careers-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-6">Mid-Career</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.15] max-w-3xl">
            中途採用 募集要項
          </h1>
          <p className="mt-8 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
            物件開拓営業ポジションの詳細な募集要項です。
          </p>
        </div>
      </section>

      {/* ─── JOB INFO TABLE ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="careers-info">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Job Details</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            募集要項
          </h2>
          <div className="bg-white border border-acoe-border">
            {JOB_INFO.map((row, i) => (
              <div key={i} className={`flex flex-col md:flex-row ${i < JOB_INFO.length - 1 ? "border-b border-acoe-border" : ""}`}>
                <div className="w-full md:w-48 bg-acoe-paper px-6 py-4 flex-shrink-0">
                  <p className="text-sm font-heading font-semibold text-acoe-charcoal">{row.label}</p>
                </div>
                <div className="px-6 py-4 flex-1">
                  <p className="text-sm text-acoe-text">{row.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SKILLS ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="careers-skills">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
            <div>
              <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Required</p>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-acoe-charcoal mb-6">必須スキル・経験</h3>
              <ul className="space-y-4">
                {REQUIRED_SKILLS.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-acoe-charcoal mt-2 flex-shrink-0" />
                    <span className="text-acoe-text leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Preferred</p>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-acoe-charcoal mb-6">歓迎スキル・経験</h3>
              <ul className="space-y-4">
                {PREFERRED_SKILLS.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-acoe-gold mt-2 flex-shrink-0" />
                    <span className="text-acoe-text leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── MID CTA ─── */}
      <section className="py-16 bg-acoe-charcoal" data-testid="careers-mid-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-white font-heading font-semibold text-lg md:text-xl">
            このポジションに興味がありますか？
          </p>
          <div className="flex gap-4">
            <Link to="/recruit/contact" className="inline-flex items-center rounded-full bg-acoe-gold text-acoe-charcoal px-6 py-3 font-heading font-semibold text-sm tracking-wide hover:bg-acoe-gold-hover transition-colors" data-testid="careers-apply-cta">
              お問い合わせ方法を見る
            </Link>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="careers-faq">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">FAQ</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            よくある質問
          </h2>
          <div className="max-w-3xl">
            {FAQ_ITEMS.map((item, i) => (
              <FAQItem key={i} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <CTABand variant="dark" />
    </div>
  );
}
